import 'url-search-params-polyfill';
import queryParamsUtils from '@utils/query-params.utils';
import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import get from 'lodash/get';
import find from 'lodash/find';
import { UseFormReturn } from 'react-hook-form';
import {
  setFieldValue,
  setRedirectedFromClients
} from '../store/reducers/form-fields/form-fields.reducer';
import formFieldsSelectors from '../store/selectors/form-fields.selectors';
import useReasons from './use-reasons.hook';
import { faqQuestionsData } from '../network/services/get-form-fields/get-form-fields.service';

interface queryParamsHookType {
  utm: { [p: string]: string } | undefined;
  goToForm: boolean;
  params: URLSearchParams;
  removeParam: (p: string) => void;
  checkRedirectedFromClients: () => void;
  question: string | null;
  setReasonsFromQuestion: (
    methods: UseFormReturn
  ) =>
    | { caseReason: string; caseDetailedReason?: string | undefined }
    | null
    | undefined;
}
const { getUtmFromParams } = queryParamsUtils;

const useQueryParams = (): queryParamsHookType => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { mainReasonValue, detailReasonValue } = useReasons();

  const reasons = useSelector(formFieldsSelectors.getReasons);

  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const removeParam = useCallback(
    (param: string) => {
      params.delete(param);

      window.history.replaceState(
        {},
        '',
        params.toString().length
          ? `${window.location.pathname}?${params}`
          : `${window.location.pathname}`
      );
    },
    [params]
  );

  const utm = useMemo(() => getUtmFromParams(params), [params]);
  const question = useMemo(() => {
    return params.get('question');
  }, [params]);

  const goToForm = useMemo(() => {
    return params.get('go_to_form') === 'true';
  }, [params]);

  const checkRedirectedFromClients = useCallback(() => {
    const redirectedFrom = params.get('redirected_from_clients');
    if (redirectedFrom) {
      dispatch(setRedirectedFromClients(redirectedFrom));
      removeParam('redirected_from_clients');
    }
    if (params.get('go_to_existing_clients') === 'true') {
      history.push('/existing-client');
      removeParam('go_to_existing_clients');
    }
  }, [dispatch, history, params, removeParam]);

  const setReasonsFromQuestion = useCallback(
    (methods: UseFormReturn) => {
      if (!question || mainReasonValue || detailReasonValue) return null;

      const questionReasons = get(faqQuestionsData(), [question], undefined);

      const faqCaseReasonOptions = get(
        reasons,
        ['case_reason', 'options'],
        undefined
      );
      const faqCaseDetailedReasonOptions = get(
        reasons,
        ['case_detailed_reason', 'options'],
        undefined
      );

      if (!questionReasons || !faqCaseReasonOptions) return null;

      const reasonToSet = find(faqCaseReasonOptions, {
        value: questionReasons.caseReason
      });
      const detailedReasonToSet = questionReasons.caseDetailedReason
        ? find(faqCaseDetailedReasonOptions[questionReasons.caseReason], {
            value: questionReasons.caseDetailedReason
          })
        : undefined;

      if (reasonToSet) {
        dispatch(
          setFieldValue({
            fieldName: 'case_reason',
            value: reasonToSet.value,
            faq_tag: reasonToSet.faq_tag
          })
        );
        methods.setValue('case_reason', reasonToSet.value);
      }
      if (detailedReasonToSet) {
        dispatch(
          setFieldValue({
            fieldName: 'case_detailed_reason',
            value: detailedReasonToSet.value,
            faq_tag: detailedReasonToSet.faq_tag
          })
        );
        methods.setValue('case_detailed_reason', detailedReasonToSet.value);
      }

      return questionReasons;
    },
    [detailReasonValue, dispatch, mainReasonValue, question, reasons]
  );

  return {
    utm,
    goToForm,
    params,
    removeParam,
    checkRedirectedFromClients,
    question,
    setReasonsFromQuestion
  };
};

export default useQueryParams;
